import {
  ButtonThemes,
  Checkbox,
  CheckCircleIcon,
  Col,
  Collapse,
  ControlLabel,
  ControlLabelPosition,
  FileTypes,
  FileUpload,
  Input,
  Modal,
  ModalSizes,
  Row,
  ThreeDotsLoader,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { fileToBase64, tipoPertencer } from 'helpers/utils/File';
import { isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import { toRequest } from 'helpers/utils/RequestUtils';
import { getAssembleiasAssessorEndpoint } from 'library/Apis';
import translation from 'locales/translation';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import {
  ChangeEvent,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ApiService from 'services/ApiService';
import {
  assembleiaAssessorActionsUpdateDocument,
  assembleiaAssessorActionsUploadDocument,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';
import {
  IAssembleiasAssessorDocumentos,
  IAssembleiasAssessorEvent,
} from 'store/modules/AssembleiasAssessorReducer';
import { IReducer } from 'store/Reducer';
import { Chip } from './components/Chip';
import Documento from './components/DocumentosModal/Documento';

interface IProps extends ModalProps {
  eventId: number;
  onConfirm: () => Promise<void>;
  registeting?: boolean;
  readAndAddOnly?: boolean;
  shouldUseBff: boolean;
  edit?: {
    file: {
      desDocumento: string;
      flgDisponivelAssociado: boolean;
      nomDocumento: string;
      oidDocumento: number;
      tipoDocumento: string;
    };
  };
}

const attachmentPrefix =
  translation.Assessor.Evento.Cadastro.Etapas.Anexo.Modal.PDF;

export const AddAttachment = (props: IProps) => {
  const [file, setFile] = useState<any>();
  const [description, setDescription] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [document, setDocument] = useState<IAssembleiasAssessorDocumentos[]>();
  const [loadDocument, setLoadDocument] = useState<boolean>(true);
  const [flagChanged, setFlagChanged] = useState<boolean>(false);

  const { showToast } = useContext(LayoutContext);
  const dispatchAction = useDispatch();

  const assembleiasAssessorEvent = useSelector<
    IReducer,
    IAssembleiasAssessorEvent
  >((state) => state.assembleiasAssessor.evento!);

  const { id, documentos } =
    assembleiasAssessorEvent && !props.readAndAddOnly
      ? assembleiasAssessorEvent
      : { id: undefined, documentos: undefined };

  const request = useCallback(
    (signal?: AbortSignal) => {
      toRequest(
        ApiService.get,
        [
          `${getAssembleiasAssessorEndpoint('1')}documento/${props.eventId}`,
          { signal },
        ],
        'assembleiaAssessorLodEvent'
      )
        .then((resp) => {
          setDocument(resp.data);
        })
        .catch(() => {})
        .finally(() => {
          setLoadDocument(false);
        });
    },
    [props.eventId]
  );

  const handleSubmit = useCallback(async () => {
    if (!file) return;

    setLoadingButton(true);
    if (!props.edit) {
      const arquivo = await fileToBase64(file);
      dispatchAction(
        assembleiaAssessorActionsUploadDocument(
          {
            arquivo,
            descricao: description,
            flgDisponivelAssociado: props.readAndAddOnly || checked,
            nome: file.name,
            oidEvento: props.eventId,
            tamanho: file.size,
            tipo: tipoPertencer(file.name),
          },
          props.shouldUseBff,
          () => {
            if (props.shouldUseBff) {
              if (props.readAndAddOnly) {
                setLoadingButton(false);
                setFile(undefined);
                setDescription('');
                setChecked(false);
                request();
              } else {
                props.onConfirm().finally(() => {
                  setLoadingButton(false);
                  props.onClose!(props.modalKey);
                });
              }
            } else {
              dispatchAction(
                assembleiasAssessorLoadEvento(props.eventId, () => {
                  setLoadingButton(false);
                  setFile(undefined);
                  setDescription('');
                  setChecked(false);
                })
              );
            }
          },
          (error) => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(error.message),
            });
            setLoadingButton(false);
          }
        )
      );
    } else {
      dispatchAction(
        assembleiaAssessorActionsUpdateDocument(
          {
            descricao: description,
            flgDisponivelAssociado: props.readAndAddOnly || checked,
            oidDocumento: props.edit.file.oidDocumento,
            idEvento: String(props.eventId),
          },
          props.shouldUseBff,
          () => {
            props.onConfirm().finally(() => {
              props.onClose!(props.modalKey);
              setFile(undefined);
            });
          },
          (error) => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(error.message),
            });
            setLoadingButton(false);
          }
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchAction, file, description, checked, props.eventId]);

  const handleClickUpdateDocumentos = () => {
    setLoadingButton(true);

    const updateDocumentPromises = document?.filter((doc) =>
      documentos?.find(
        (prevDoc) =>
          prevDoc.oidDocumento === doc.oidDocumento &&
          (doc.desDocumento !== prevDoc.desDocumento ||
            doc.flgDisponivelAssociado !== prevDoc.flgDisponivelAssociado)
      )
    );

    const updatePromises = updateDocumentPromises?.map((documento) => {
      if (!documento) return Promise.resolve();

      const { desDocumento, flgDisponivelAssociado, oidDocumento } = documento;

      return dispatchAction(
        assembleiaAssessorActionsUpdateDocument(
          {
            descricao: desDocumento,
            flgDisponivelAssociado,
            oidDocumento,
            idEvento: String(props.eventId),
          },
          false
        )
      );
    });

    Promise.all(updatePromises || [])
      .then(() =>
        dispatchAction(
          assembleiasAssessorLoadEvento(props.eventId, () => {
            setLoadingButton(false);
          })
        )
      )
      .catch((error) => {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label:
            translation.Toasts.Errors.Keys[
              `${
                (error as AxiosError<Error>).response?.data.message ||
                (error as AxiosError<Error>).response?.data ||
                (error as AxiosError<Error>).message
              }` as keyof typeof translation.Toasts.Errors.Keys
            ] ?? translation.Toasts.Errors.defaultAlternative,
        });
      });
  };

  useEffect(() => {
    if (props.edit) {
      setFile(props.edit?.file);
      setDescription(props.edit?.file.desDocumento);
      setChecked(props.edit?.file.flgDisponivelAssociado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!props.readAndAddOnly && documentos && documentos.length) {
      setDocument(documentos);
    }
  }, [documentos, props.readAndAddOnly]);

  useEffect(() => {
    if (!props.readAndAddOnly) {
      const changed = document?.some((doc) => {
        const initialDoc = documentos?.find(
          (initialDoc) => initialDoc.oidDocumento === doc.oidDocumento
        );

        return (
          doc.desDocumento !== initialDoc?.desDocumento ||
          doc.flgDisponivelAssociado !== initialDoc?.flgDisponivelAssociado
        );
      });

      setFlagChanged(changed || false);
    }
  }, [document, documentos, props.readAndAddOnly]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setLoadDocument(true);

    if (props.readAndAddOnly) {
      request(signal);
    }

    return () => {
      controller.abort();
    };
  }, [dispatchAction, props.eventId, props.readAndAddOnly, request]);

  return (
    <Modal
      title={props.edit!?.file! ? 'Editar documento' : 'Adicionar documento'}
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={props.onClose}
      size={ModalSizes.Large}
      footer={[
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <TimesCircleIcon />,
          children: 'Cancelar',
          onClick: () => props.onClose!(props.modalKey),
        },
        {
          theme: ButtonThemes.Primary,
          leftIcon: <CheckCircleIcon />,
          children: 'Salvar',
          loading: loadingButton,
          disabled: file
            ? loadingButton ||
              (props.edit &&
                props.edit.file.flgDisponivelAssociado === checked &&
                ((props.edit && props.edit.file.desDocumento) === description ||
                  (props.edit &&
                    props.edit.file.desDocumento === null &&
                    description === '')))
            : !props.readAndAddOnly
            ? loadingButton || !flagChanged
            : true,
          onClick: () =>
            file ? handleSubmit() : handleClickUpdateDocumentos(),
        },
      ]}
    >
      <Row>
        <Collapse opened={!file} animateOpacity>
          <FileUpload
            multiple={false}
            onChange={(evt: File) => {
              if ((evt as File).size > 25000000) {
                showToast({
                  pauseOnFocusLoss: true,
                  theme: ToastTypes.Error,
                  showStatusBar: true,
                  timeout: 4000,
                  label: 'O limite de tamanho é de 25 MB!',
                });
              } else {
                setFile(evt);
              }
            }}
            hideTopLabel
            hideHelperText
            label={attachmentPrefix.UploadLabel()}
            allowedFileTypes={[
              FileTypes.PDF,
              FileTypes.JPG,
              FileTypes.JPEG,
              FileTypes.PNG,
            ]}
          />
        </Collapse>
        <Collapse opened={Boolean(file)} animateOpacity>
          <Row>
            <Col>
              <Chip
                label={(file && file.name) || (file && file.nomDocumento)}
                onClick={() =>
                  props.edit!?.file! ? undefined : setFile(undefined)
                }
                icon={props.edit!?.file! ? undefined : <TimesCircleIcon />}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Input
                label={'Descrição'}
                placeholder={'Descrição do arquivo'}
                disabled={!file || loadingButton}
                value={description ?? ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setDescription(e.target.value)
                }
              />
            </Col>
            <Col noPaddingTop>
              <ControlLabel
                label={'Visível para associados'}
                control={
                  <Checkbox
                    disabled={!file || loadingButton || props.readAndAddOnly}
                    value={props.readAndAddOnly || checked}
                    onChange={() => setChecked((e: boolean) => !e)}
                  />
                }
                labelPosition={ControlLabelPosition.Right}
              />
            </Col>
          </Row>
        </Collapse>
        <Collapse opened={props.readAndAddOnly && loadDocument} animateOpacity>
          <Col centralized>
            <ThreeDotsLoader />
          </Col>
        </Collapse>
      </Row>
      <Row>
        <Collapse
          opened={props.readAndAddOnly ? !file && !loadDocument : !file}
          animateOpacity
        >
          {!props.registeting && !isNullOrUndefined(id) ? (
            <Row style={{ marginTop: '24px' }}>
              {documentos && documentos.length ? (
                <h4>Documentos</h4>
              ) : (
                <Fragment />
              )}
              {documentos &&
                documentos.map((documento: any) => (
                  <Documento
                    idEvento={id}
                    key={documento.oidDocumento}
                    onFlagChange={(updatedDocument: any) => {
                      setDocument((prevDocuments: any) => {
                        const updatedDocumentIndex = prevDocuments.findIndex(
                          (doc: any) =>
                            doc.oidDocumento === updatedDocument.oidDocumento
                        );
                        if (updatedDocumentIndex !== -1) {
                          const updatedDocuments = [...prevDocuments];
                          updatedDocuments[updatedDocumentIndex] =
                            updatedDocument;
                          return updatedDocuments;
                        }
                        return prevDocuments;
                      });
                    }}
                    {...documento}
                  />
                ))}
            </Row>
          ) : props.readAndAddOnly ? (
            <Row style={{ marginTop: '24px' }}>
              {document && document.length ? <h4>Documentos</h4> : <Fragment />}
              {document &&
                document.map((document: any) => (
                  <Documento
                    idEvento={props.eventId}
                    key={document.oidDocumento}
                    readAndAddOnly={props.readAndAddOnly}
                    onFlagChange={(updatedDocument: any) => {
                      setDocument((prevDocuments: any) => {
                        const updatedDocumentIndex = prevDocuments.findIndex(
                          (doc: any) =>
                            doc.oidDocumento === updatedDocument.oidDocumento
                        );
                        if (updatedDocumentIndex !== -1) {
                          const updatedDocuments = [...prevDocuments];
                          updatedDocuments[updatedDocumentIndex] =
                            updatedDocument;
                          return updatedDocuments;
                        }
                        return prevDocuments;
                      });
                    }}
                    {...document}
                  />
                ))}
            </Row>
          ) : undefined}
        </Collapse>
      </Row>
    </Modal>
  );
};
